import { useCallback, useSyncExternalStore } from "react";
import { QueryClient, QueryKey, useQueryClient } from "@tanstack/react-query";
import { actAsCacheKey, getActAsOrgId } from "../contexts/actAsStore";
import { useProfileContext } from "../contexts/ProfileContext";
import { BaseLogo, getLogoOrgId } from "../core/models/entities/BaseLogo";
import { ErrorStatus } from "../core/models/entities/ErrorStatus";
import { useApiClient } from "../data/clients/ApiClient";
import {
  CartPriceCheckRequestDto,
  CartPriceCheckStatusDto,
} from "../data/models/responses/CartPriceCheckDto";
import { mapToLogo } from "../data/models/mappers/mapLogo";
import { patchLogoPricesInLists } from "../data/repositories/cartPriceListSync";
import { hasAdjustedPrices } from "../data/repositories/designCachePatch";
import { enviroment } from "../enviroment";
import {
  CartItem,
  CartItemDesign,
  CartItemLogo,
} from "../presentation/screens/order-create/models/CartItem";
import { collectCartLogoIds } from "../presentation/screens/order-confirmation/storageVerify";
import {
  CART_PRICE_CHECK_DAILY_CAP_PAUSE_S,
  CART_PRICE_CHECK_DEFAULT_COOLDOWN_S,
  CART_PRICE_CHECK_MAX_LOGOS_PER_ADD,
  CART_PRICE_CHECK_TIMEOUT_MS,
  CART_PRICE_CHECK_TRANSPORT_PAUSE_MS,
  CART_PRICE_CHECK_UNAVAILABLE_PAUSE_MS,
  CART_PRICE_CHECK_UPDATED_TTL_MS,
  cartPriceCheckFirstPollDelay,
  cartPriceCheckPollDelay,
  cartPriceCheckSeconds,
  isCartPriceCheckDeterministicError,
  PriceRowFields,
  priceFieldsDiffer,
  selectCartPriceCheckLogoIds,
} from "../utils/functions/cartPriceCheck";
import { resolveStorageFreshnessOrgId } from "../utils/functions/storageFreshness";

// Cart price check (flag REACT_APP_CART_PRICE_CHECK_ENABLED; Nichlas 12/9:
// "the moment something is put in the cart we check its price, without it
// costing performance or speed"). The cart line is added AT ONCE with the
// prices the page already knows - nothing waits, nothing blocks - and
// afterwards ONE fire-and-forget POST organizations/{orgId}/logos/{logoId}/
// price-check leaves per logo just added (a design = its logos, deduped,
// at most 10). The API puts the serial on its cart-price-check queue and
// its worker runs ONE supplier call per 3 s batch window for every serial
// added meanwhile; the request path itself never touches NAV or the
// supplier. After a 202 queued this module reads the light status GET on
// a bounded 3/8/15 s chain (<= 3 calls; the first tick waits for the API's
// pollAfterSeconds when longer) and, ONLY when the API says
// "updated", invalidates the cart's own detail entry of that logo
// (["logos", actAs, id] for a logo line, ["designs", actAs, designId] for a
// design line) with refetchType "all": useCartSync's observers are live
// app-wide, so the ONE GetLogo / GetDesign that follows lands the fresh
// prices in the cart line through updateInCart, and its price diff marks
// the discreet "Price updated" note - and, with the list-sync sub-flag
// (REACT_APP_CART_PRICE_CHECK_LIST_SYNC_ENABLED), useCartSync copies the
// landed prices into the cached list rows of the same logo/design as well
// (data/repositories/cartPriceListSync): 0 calls, so the logo list, the
// popup's list stub and the design list the user returns to show the fresh
// price without a reload. A logo that is in the cart ONLY as a placed logo
// of a design line has no GetLogo of its own in that chain (GetDesign
// prices it in the design's context, which never reaches the logo lists -
// skeptic round 2), so on "updated" the sub-flag also fetches the logo's
// OWN detail ONCE (landPlacedLogoInLists: fetchQuery of ["logos", actAs,
// id] - the popup's entry, a list stub is replaced) and patches the logo
// lists from that answer. Silence on every other outcome: no
// toast, no throw, errors go to console.debug.
//
// Module state, shared by every cart provider mount: the hold per logo
// (session dedupe - the same logo added three times inside the API's
// cooldown = ONE call), the price rows the user SAW when the check left
// (the note compares the fresh answer against them, not against the cart
// copy, which the ordinary 30 s sync may already have refreshed), the logos
// with a status poll running, the ones the API reported updated (with the
// instant - awaiting the re-read, consumed by it whatever it finds, void
// after 2 min), the ones whose price really changed (the note) and the one
// global pause (transport trouble 60 s, 404 = API flag off 5 min, dailyCap
// = the API's retryAfterSeconds).

type RequestCheck = (
  orgId: number,
  logoId: number,
  signal?: AbortSignal
) => Promise<CartPriceCheckRequestDto>;

type StatusCheck = (
  orgId: number,
  logoId: number,
  signal?: AbortSignal
) => Promise<CartPriceCheckStatusDto>;

type LogoRead = (orgId: number, logoId: number) => Promise<BaseLogo>;

export interface CartPriceCheckDeps {
  request: RequestCheck;
  status: StatusCheck;
  /** GetLogo mapped to the domain - the placed-logo re-read on "updated" (list-sync sub-flag only). */
  getLogo: LogoRead;
  queryClient: QueryClient;
}

/**
 * One logo to check, the organization its detail is read under
 * (getLogoOrgId, fallback the cart's org) and the price rows the user saw
 * when the line went into the cart (the note's baseline; undefined when
 * the line carried none).
 */
export interface CartPriceCheckTarget {
  logoId: number;
  orgId: number;
  prices?: readonly PriceRowFields[];
}

export type CartPriceCheckPauseKind =
  | "none"
  | "failed"
  | "unavailable"
  | "dailyCap";

const heldUntilMs = new Map<number, number>();
const seenRows = new Map<number, readonly PriceRowFields[]>();
const checking = new Set<number>();
const updated = new Map<number, number>();
const priceChanged = new Set<number>();
const pollTimers = new Map<number, ReturnType<typeof setTimeout>>();
let pausedUntilMs = 0;
let pauseKind: CartPriceCheckPauseKind = "none";
let liveItems: readonly CartItem[] = [];
let version = 0;
const listeners = new Set<() => void>();

function notify(): void {
  version += 1;
  listeners.forEach((listener) => listener());
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function getSnapshot(): number {
  return version;
}

export function resetCartPriceCheckForTests(): void {
  pollTimers.forEach((timer) => clearTimeout(timer));
  pollTimers.clear();
  heldUntilMs.clear();
  seenRows.clear();
  checking.clear();
  updated.clear();
  priceChanged.clear();
  pausedUntilMs = 0;
  pauseKind = "none";
  liveItems = [];
  version = 0;
  listeners.clear();
}

/**
 * Tests only: pretend the API reported the logo "updated" `ageMs` ago (the
 * mark landCartPriceCheck turns into the note), optionally with the price
 * rows the user saw when the check left.
 */
export function seedCartPriceCheckUpdatedForTests(
  logoId: number,
  seen?: readonly PriceRowFields[],
  ageMs = 0
): void {
  updated.set(logoId, Date.now() - ageMs);
  if (seen !== undefined) seenRows.set(logoId, seen);
}

/** Tests / diagnostics: whether the logo carries a live "updated" mark awaiting its landing. */
export function isCartPriceCheckMarked(logoId: number): boolean {
  return updated.has(logoId);
}

/** The global pause in effect (tests / diagnostics): kind and the instant it ends. */
export function getCartPriceCheckPause(): {
  kind: CartPriceCheckPauseKind;
  untilMs: number;
} {
  return { kind: pauseKind, untilMs: pausedUntilMs };
}

/** Whether a status poll is running for the logo. */
export function isCartPriceChecking(logoId: number): boolean {
  return checking.has(logoId);
}

/** The instant the session hold of the logo ends (ms since epoch), undefined when it has none. */
export function getCartPriceCheckHeldUntilMs(logoId: number): number | undefined {
  return heldUntilMs.get(logoId);
}

/** True when the cart price check landed a REAL price change for this logo - the "Price updated" note. */
export function isCartPriceCheckUpdated(logoId: number): boolean {
  return priceChanged.has(logoId);
}

/** True when any of the logos (a design's) carries the note. */
export function isAnyCartPriceCheckUpdated(logoIds: readonly number[]): boolean {
  return logoIds.some((id) => priceChanged.has(id));
}

/** Re-renders the caller whenever the module state changes (the note, the polls). */
export function useCartPriceCheckVersion(): number {
  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}

/**
 * useCartSync calls this for EVERY logo whose cart entry's fresh detail
 * landed (a logo line: its logo; a design line: each placed logo), with the
 * logo's fresh price rows (`freshRows`, undefined when the answer carries
 * none) and whether the entry's prices differed from the cart copy
 * (`differedFromCart`). Only a logo the API reported "updated" - the
 * re-read this module asked for - can turn into the note, and the mark is
 * consumed by that landing whatever it finds, so a later, unrelated diff
 * (the 30 s staleTime re-read after a list-price feed) never wears the
 * check's note. The note itself compares the fresh rows against the rows
 * the user SAW when the check left (seenRows): the ordinary sync may have
 * landed the new price in the line before the poll reported it, and the
 * change is still real to the user. Without a baseline (a line that
 * carried no rows) the cart-copy diff decides. Fresh rows without a
 * single ADJUSTED row (empty, or persisted-only rows with adjustedPrice 0
 * - AdjustPrice did not run: a transient price failure, a pending
 * product) carry no information, exactly as the design cache patch treats
 * them (designCachePatch, PRICES): never the note. A mark older than
 * CART_PRICE_CHECK_UPDATED_TTL_MS is void. Flag off = no-op.
 */
export function landCartPriceCheck(
  logoId: number,
  freshRows: readonly PriceRowFields[] | undefined,
  differedFromCart: boolean
): void {
  if (!enviroment.cartPriceCheckEnabled) return;
  const markedAtMs = updated.get(logoId);
  if (markedAtMs === undefined) return;
  updated.delete(logoId);
  if (Date.now() - markedAtMs > CART_PRICE_CHECK_UPDATED_TTL_MS) return;
  const seen = seenRows.get(logoId);
  let changed: boolean;
  if (freshRows === undefined) changed = differedFromCart;
  else if (!hasAdjustedPrices(freshRows)) changed = false;
  else if (seen !== undefined) changed = priceFieldsDiffer(seen, freshRows);
  else changed = differedFromCart;
  if (!changed) return;
  priceChanged.add(logoId);
  notify();
}

/**
 * The landing without rows: the entry's prices differed from the cart
 * copy - the note when the logo carries a live "updated" mark (see
 * landCartPriceCheck). Flag off = no-op.
 */
export function markCartPriceChanged(logoId: number): void {
  landCartPriceCheck(logoId, undefined, true);
}

/**
 * Drops the note (and the pending "updated" mark) of the given logos - the
 * cart provider calls it with the logos a removed line took out of the
 * cart entirely (a logo still shown by another line keeps its note) - or
 * of every logo when called without ids (clearCart). The session hold and
 * the seen rows stay: a removed and re-added logo inside the cooldown still
 * costs nothing, and a poll still running lands against the same
 * baseline. A running poll is left alone; its next tick stops on its own
 * when the logo is no longer in the cart. Flag off = no-op (the sets are
 * empty anyway).
 */
export function clearCartPriceCheckUpdated(logoIds?: readonly number[]): void {
  if (!enviroment.cartPriceCheckEnabled) return;
  let changed = false;
  if (logoIds === undefined) {
    changed = updated.size > 0 || priceChanged.size > 0;
    updated.clear();
    priceChanged.clear();
  } else {
    logoIds.forEach((id) => {
      if (updated.delete(id)) changed = true;
      if (priceChanged.delete(id)) changed = true;
    });
  }
  if (changed) notify();
}

// The ErrorStatus of a DataError (AuthorizedFetch); undefined for transport
// errors (fetch rejected, aborted) and anything else that is not ours.
function errorStatusOf(error: unknown): ErrorStatus | undefined {
  const status = (error as { errorStatus?: ErrorStatus } | null | undefined)
    ?.errorStatus;
  return status && typeof status.code === "number" ? status : undefined;
}

// Runs one call under the 8 s transport budget.
function withTimeout<T>(run: (signal: AbortSignal) => Promise<T>): Promise<T> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), CART_PRICE_CHECK_TIMEOUT_MS);
  return run(controller.signal).finally(() => clearTimeout(timer));
}

/**
 * The cart entries the price of `logoId` is shown through, in the act-as
 * scope `scope`: ["logos", scope, id] for every logo line of that logo,
 * ["designs", scope, designId] for every design line that places it.
 * Distinct. Empty = the logo is no longer in the cart.
 */
export function cartPriceCheckKeysFor(
  logoId: number,
  items: readonly CartItem[],
  scope: number
): QueryKey[] {
  const keys: QueryKey[] = [];
  const seen = new Set<string>();
  const push = (key: QueryKey) => {
    const signature = JSON.stringify(key);
    if (seen.has(signature)) return;
    seen.add(signature);
    keys.push(key);
  };
  items.forEach((item) => {
    if (item.type === "Logo") {
      const line = item as CartItemLogo;
      if ((line.baseLogo?.id ?? line.logoId) === logoId) {
        push(["logos", scope, logoId]);
      }
    } else if (item.type === "Design") {
      const line = item as CartItemDesign;
      if (line.design?.id > 0 && collectCartLogoIds([line]).includes(logoId)) {
        push(["designs", scope, line.design.id]);
      }
    }
  });
  return keys;
}

/**
 * The logos of the items just added, each with the organization its
 * detail is read under - getLogoOrgId (the logo's customer, else its main
 * organization) exactly as the admin "refresh prices now" call, since the
 * API scopes the route to the logo's own organizations (main / attached /
 * global) and a vendor's OWN org would be outside that scope for a
 * customer's logo; a global logo (org 0) falls back to `fallbackOrgId`
 * (the cart's org - in scope for every org). Ids and order as
 * collectCartLogoIds (standalone logo = its id; design = its placed logos
 * and the instance copies, deduped, first occurrence wins).
 */
export function collectCartPriceCheckTargets(
  items: readonly CartItem[],
  fallbackOrgId: number
): CartPriceCheckTarget[] {
  const orgOf = new Map<number, number>();
  const rowsOf = new Map<number, readonly PriceRowFields[]>();
  const note = (
    logo:
      | { id?: number; product?: { prices?: readonly PriceRowFields[] } | null }
      | undefined,
    id: number | undefined
  ) => {
    const logoId = logo?.id ?? id;
    if (logoId == null || !(logoId > 0) || orgOf.has(logoId)) return;
    const own = logo ? getLogoOrgId(logo as Parameters<typeof getLogoOrgId>[0]) : 0;
    orgOf.set(logoId, own > 0 ? own : fallbackOrgId);
    const rows = logo?.product?.prices;
    if (rows) rowsOf.set(logoId, rows);
  };
  items.forEach((item) => {
    if (item.type === "Logo") {
      const line = item as CartItemLogo;
      note(line.baseLogo, line.logoId);
    } else if (item.type === "Design") {
      const line = item as CartItemDesign;
      line.design?.designLogos?.forEach((dl) => note(dl.logo, dl.logoId));
      line.instances?.forEach((inst) =>
        inst.logos?.forEach((l) => note(l.baseLogo, l.logoId))
      );
    }
  });
  return collectCartLogoIds(items as CartItem[]).map((logoId) => ({
    logoId,
    orgId: orgOf.get(logoId) ?? fallbackOrgId,
    prices: rowsOf.get(logoId),
  }));
}

function stopChecking(logoId: number): void {
  const timer = pollTimers.get(logoId);
  if (timer !== undefined) {
    clearTimeout(timer);
    pollTimers.delete(logoId);
  }
  if (checking.delete(logoId)) notify();
}

/**
 * The price follows to the list for a logo that is in the cart ONLY as a
 * placed logo of design lines (sub-flag cartPriceCheckListSyncEnabled;
 * skeptic round 3). The check's "updated" re-reads the DESIGN entry, and
 * that answer prices the logo in the design's vendor+customer context -
 * never the rows GetLogo/GetAllLogos answer with (round 2) - so nothing in
 * the chain would refresh the logo's own logo-list row, customer-tab row or
 * popup entry. This fires the ONE GetLogo that does: fetchQuery of the
 * detail entry ["logos", scope, id] in the organization the check read the
 * logo under (the popup's own path - LogoDetailsWrapper reads the same key
 * with getLogoOrgId), staleTime 0 (always the server, a popup's list-row
 * stub is replaced), retry off (exactly one call), deduped by react-query
 * with a fetch of that entry already in flight (an open popup's own load).
 * The landed logo is then copied into every cached logo list page of the
 * scope (patchLogoPricesInLists - freshness kept, 0 further calls). A plain
 * invalidateQueries would not do: it never seeds an absent entry (the
 * common case - a design added from the design list, popup never opened),
 * skips an entry whose only observers are disabled, and no observer of a
 * placed logo's entry patches the lists on its landing (useCartSync walks
 * the cart's logo LINES only). Never awaited by anyone; a failure is
 * console.debug and the lists keep their rows (the cart line itself is
 * landed by the design re-read as before). Sub-flag off = never called.
 * Call budget: 1 GetLogo per placed logo, ONLY on "updated" (<= 10 per
 * add via the request cap, the API's cooldown between adds), 0 on render,
 * page or list view.
 */
function landPlacedLogoInLists(
  orgId: number,
  logoId: number,
  scope: number,
  deps: CartPriceCheckDeps
): void {
  const queryKey = ["logos", scope, logoId];
  deps.queryClient
    .fetchQuery({
      queryKey,
      queryFn: () => deps.getLogo(orgId, logoId),
      staleTime: 0,
      retry: false,
    })
    .then((logo) => {
      // The act-as identity switched while the read was out: the entry is
      // the old scope's (its key says so), the lists on screen are not.
      if (scope !== actAsCacheKey()) return;
      const landedAtMs =
        deps.queryClient.getQueryState(queryKey)?.dataUpdatedAt || Date.now();
      patchLogoPricesInLists(deps.queryClient, logo, landedAtMs);
    })
    .catch((error: unknown) => {
      console.debug("[CartPriceCheck] placed logo re-read failed", logoId, error);
    });
}

// One status poll `attempt` (0-based) for the logo, `delay` ms from now
// (the schedule's entry, or `firstDelay` for the first tick - the API's
// pollAfterSeconds pushed it back); the budget spent = the poll simply
// ends (the next cart mount's 30 s staleTime re-read and the order path's
// price refresh pick up whatever the worker landed later).
function scheduleTick(
  orgId: number,
  logoId: number,
  scope: number,
  attempt: number,
  deps: CartPriceCheckDeps,
  firstDelay?: number
): void {
  const delay =
    attempt === 0 && firstDelay !== undefined
      ? firstDelay
      : cartPriceCheckPollDelay(attempt, enviroment.cartPriceCheckPollMs);
  if (delay === undefined) {
    stopChecking(logoId);
    return;
  }
  const timer = setTimeout(() => {
    pollTimers.delete(logoId);
    if (!checking.has(logoId)) return;
    // The logo left the cart, or the act-as identity switched (another
    // cart, another price scope): nothing to land the answer in, 0 calls.
    if (
      scope !== actAsCacheKey() ||
      cartPriceCheckKeysFor(logoId, liveItems, scope).length === 0
    ) {
      stopChecking(logoId);
      return;
    }
    withTimeout((signal) => deps.status(orgId, logoId, signal))
      .then((body) => {
        if (!checking.has(logoId)) return;
        const state = body?.state;
        if (state === "updated") {
          // Mark first, then ONE refetch per cart entry showing this logo
          // (normally one): useCartSync's observer is live, the answer
          // lands there (landCartPriceCheck) and consumes the mark.
          updated.set(logoId, Date.now());
          const keys = cartPriceCheckKeysFor(logoId, liveItems, scope);
          keys.forEach((queryKey) => {
            void deps.queryClient.invalidateQueries({
              queryKey,
              exact: true,
              refetchType: "all",
            });
          });
          // Placed in design lines only (no ["logos", scope, id] among the
          // keys): the logo's own lists need its own GetLogo - ONE, here.
          // A logo line's own re-read (the invalidation above) lands in
          // useCartSync and patches the lists from there: nothing extra.
          if (
            enviroment.cartPriceCheckListSyncEnabled &&
            !keys.some((key) => key[0] === "logos")
          ) {
            landPlacedLogoInLists(orgId, logoId, scope, deps);
          }
          stopChecking(logoId);
          return;
        }
        if (state === "queued") {
          scheduleTick(orgId, logoId, scope, attempt + 1, deps);
          return;
        }
        // unchanged | noPrices | shrunkKept | failed | none | unknown: done.
        stopChecking(logoId);
      })
      .catch((error: unknown) => {
        console.debug("[CartPriceCheck] status failed", logoId, error);
        stopChecking(logoId);
      });
  }, delay);
  pollTimers.set(logoId, timer);
}

function onAccepted(
  orgId: number,
  logoId: number,
  scope: number,
  body: CartPriceCheckRequestDto | undefined,
  deps: CartPriceCheckDeps
): void {
  const now = Date.now();
  // A 202 of any kind proves the route is on: the 404 window ends here -
  // one that ran out (this call was its probe) as well as one still
  // running because a per-logo 404 of the same add landed BEFORE this
  // answer - so the next add asks in full again.
  if (pauseKind === "unavailable") {
    pauseKind = "none";
    pausedUntilMs = 0;
  }
  if (body?.queued === true) {
    heldUntilMs.set(
      logoId,
      now +
        cartPriceCheckSeconds(
          body.cooldownSeconds,
          CART_PRICE_CHECK_DEFAULT_COOLDOWN_S
        ) *
          1000
    );
    checking.add(logoId);
    notify();
    // The first read waits for the API's batch window (pollAfterSeconds)
    // when that is longer than the schedule's 3 s; the chain and its
    // budget (<= 3 calls) stay.
    scheduleTick(
      orgId,
      logoId,
      scope,
      0,
      deps,
      cartPriceCheckFirstPollDelay(
        body.pollAfterSeconds,
        enviroment.cartPriceCheckPollMs
      )
    );
    return;
  }
  // queued:false - silence, only the holds move.
  switch (body?.reason) {
    case "cooldown":
      // Another tab / a colleague asked first: the API's claim is the truth.
      heldUntilMs.set(
        logoId,
        now +
          cartPriceCheckSeconds(
            body.retryAfterSeconds,
            CART_PRICE_CHECK_DEFAULT_COOLDOWN_S
          ) *
            1000
      );
      return;
    case "dailyCap":
      pausedUntilMs = Math.max(
        pausedUntilMs,
        now +
          cartPriceCheckSeconds(
            body.retryAfterSeconds,
            CART_PRICE_CHECK_DAILY_CAP_PAUSE_S
          ) *
            1000
      );
      pauseKind = "dailyCap";
      return;
    case "notNumericSerial":
      // Deterministic for the logo (name/number logo): never ask again this session.
      heldUntilMs.set(logoId, Number.POSITIVE_INFINITY);
      return;
    default:
      // productPending / unknown: the default hold set at the request stands.
      return;
  }
}

function onRejected(logoId: number, error: unknown): void {
  const status = errorStatusOf(error);
  const now = Date.now();
  if (status?.code === 404) {
    // The API flag is off (or the logo is outside the route's org): no add
    // asks for five minutes, then ONE probe per window - the first logo of
    // the first add after it (requestCartPriceChecks); a 202 ends the
    // window (onAccepted), a 404 opens the next one here. The hold on this
    // logo stands, so a per-logo 404 cannot be re-asked every window.
    pausedUntilMs = Math.max(pausedUntilMs, now + CART_PRICE_CHECK_UNAVAILABLE_PAUSE_MS);
    pauseKind = "unavailable";
  } else if (isCartPriceCheckDeterministicError(status?.code)) {
    // Any other 4xx (400: no access to the logo's org, ...) is
    // deterministic for THIS logo: the hold stands, so the session does
    // not ask again inside the cooldown, and nothing else pauses - the
    // next logo may well be fine.
  } else {
    // Transport trouble, the 8 s timeout, a 5xx (503 queueUnavailable: the
    // API released its claim): the request may never have reached the
    // queue - release the hold, pause a minute.
    heldUntilMs.delete(logoId);
    pausedUntilMs = Math.max(pausedUntilMs, now + CART_PRICE_CHECK_TRANSPORT_PAUSE_MS);
    pauseKind = "failed";
  }
  console.debug("[CartPriceCheck] request failed", logoId, status?.code ?? error);
}

/**
 * ONE fire-and-forget POST per logo just added - or none. Never called on
 * render or from an effect: only an add costs a call. Nothing while the
 * global pause stands; logos held by the session dedupe (asked for within
 * the API's cooldown - by this add or an earlier one) are dropped; what is
 * left is held BEFORE the call leaves (a second add in the same tick costs
 * nothing) and capped at CART_PRICE_CHECK_MAX_LOGOS_PER_ADD - at ONE, the
 * probe, for the first add after a 404 window (the API flag off): the rest
 * of that add is not held, so a later add asks for them once the probe
 * proved the route; an API flag still off costs 1 POST per window, never
 * 10, and every 404 is a failed request on the alarm. A logo whose
 * call leaves starts over: its baseline becomes the rows just added and
 * an earlier note / mark is dropped (a re-add INSIDE the cooldown sends
 * nothing and keeps its note - the line's price is still the updated
 * one). The promise is never awaited and never rejects into the caller.
 * Returns how many calls left.
 */
export function requestCartPriceChecks(
  targets: readonly CartPriceCheckTarget[],
  deps: CartPriceCheckDeps
): number {
  const now = Date.now();
  if (now < pausedUntilMs) return 0;
  const cap =
    pauseKind === "unavailable" ? 1 : CART_PRICE_CHECK_MAX_LOGOS_PER_ADD;
  const orgOf = new Map<number, number>();
  const rowsOf = new Map<number, readonly PriceRowFields[] | undefined>();
  targets.forEach((target) => {
    if (target.orgId > 0 && !orgOf.has(target.logoId)) {
      orgOf.set(target.logoId, target.orgId);
      rowsOf.set(target.logoId, target.prices);
    }
  });
  const ids = selectCartPriceCheckLogoIds(
    targets.map((target) => target.logoId).filter((id) => orgOf.has(id)),
    heldUntilMs,
    now,
    cap
  );
  if (ids.length === 0) return 0;
  const scope = actAsCacheKey();
  const holdUntil = now + CART_PRICE_CHECK_DEFAULT_COOLDOWN_S * 1000;
  let noteChanged = false;
  ids.forEach((logoId) => {
    const orgId = orgOf.get(logoId) as number;
    heldUntilMs.set(logoId, holdUntil);
    const rows = rowsOf.get(logoId);
    if (rows !== undefined) seenRows.set(logoId, rows);
    else seenRows.delete(logoId);
    updated.delete(logoId);
    if (priceChanged.delete(logoId)) noteChanged = true;
    withTimeout((signal) => deps.request(orgId, logoId, signal))
      .then((body) => onAccepted(orgId, logoId, scope, body, deps))
      .catch((error: unknown) => onRejected(logoId, error));
  });
  if (noteChanged) notify();
  return ids.length;
}

/**
 * The cart price check as the cart provider uses it: hands back a function
 * that, given the items just added, fires ONE fire-and-forget POST per
 * distinct logo (a standalone logo = its id; a design = its logos, deduped,
 * at most 10) in the organization each logo is read under
 * (collectCartPriceCheckTargets), with the cart's own org
 * (resolveStorageFreshnessOrgId, read at call time - so is the act-as
 * scope) as the fallback for global logos, and polls the status afterwards.
 * `items` = the live cart, mirrored into the module so a poll tick can stop
 * when its logo left the cart and can name the cart entries to refetch.
 * Flag off = the function returns at once and nothing is mirrored: no
 * call, no state, no timer.
 * Call budget: at most 1 POST per logo per add (<= 10 per add), 0 for an
 * add whose logos were all asked for within the cooldown, 1 (the probe)
 * for the first add after a 404 window; then <= 3 status GET per queued
 * logo and <= 1 GetLogo / GetDesign, only when "updated" - plus, with the
 * list-sync sub-flag, <= 1 GetLogo for a logo placed in design lines only
 * (landPlacedLogoInLists), also only when "updated".
 */
export function useCartPriceCheckOnAdd(
  items: readonly CartItem[]
): (added: readonly CartItem[]) => void {
  const queryClient = useQueryClient();
  const { requestCartPriceCheck, getCartPriceCheckStatus, getLogo } =
    useApiClient();
  const { role, organizationId } = useProfileContext();
  if (enviroment.cartPriceCheckEnabled) liveItems = items;
  return useCallback(
    (added: readonly CartItem[]) => {
      if (!enviroment.cartPriceCheckEnabled) return;
      const fallbackOrgId = resolveStorageFreshnessOrgId(
        getActAsOrgId(),
        role,
        organizationId
      );
      if (!(fallbackOrgId > 0)) return;
      requestCartPriceChecks(collectCartPriceCheckTargets(added, fallbackOrgId), {
        request: requestCartPriceCheck,
        status: getCartPriceCheckStatus,
        getLogo: (orgId, logoId) => getLogo(orgId, logoId).then(mapToLogo),
        queryClient,
      });
    },
    [
      role,
      organizationId,
      requestCartPriceCheck,
      getCartPriceCheckStatus,
      getLogo,
      queryClient,
    ]
  );
}
